import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { useVendorDirectoryPermissions } from "../../hooks/useVendorDirectoryPermissions";
import {
  deleteVendorDirectoryCollectionSource,
  listVendorDirectoryCollectionSources,
  previewVendorDirectoryCollectionSourceDeletion,
  updateVendorDirectoryCollectionSource,
} from "../../api/clientVendorDirectoryCollectionApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import {
  hasVendorDirectoryPermission,
  VENDOR_DIRECTORY_PERMISSIONS,
} from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.sourcesPage";

function healthBadgeClass(status) {
  if (status === "healthy") return "cvd-badge--published";
  if (status === "blocked" || status === "failing") return "cvd-badge--suspended";
  if (status === "degraded" || status === "disabled") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function localizedLabel(t, exists, group, value) {
  if (!value) return "—";
  const key = `${COPY_PREFIX}.${group}.${value}`;
  return exists(key) ? t(key) : formatStatusLabel(value);
}

function sourceField(t, exists, source, field, fallback = "") {
  const key = `${COPY_PREFIX}.catalog.${source.sourceKey}.${field}`;
  if (exists(key)) return t(key);
  return fallback;
}

function SourceManageModal({
  source,
  clientViewId,
  canUpdate,
  canActivate,
  canDeactivate,
  onClose,
  onUpdated,
  onDeleted,
  t,
  exists,
  locale,
}) {
  const closeButtonRef = useRef(null);
  const titleId = "cvd-source-modal-title";
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  const displayName = sourceField(t, exists, source, "name", source.name);
  const summary =
    sourceField(t, exists, source, "summary", "") ||
    source.management?.useSummary ||
    source.description ||
    t(`${COPY_PREFIX}.catalogFallback`);
  const detail = sourceField(t, exists, source, "detail", "") || source.management?.useDetail || "";
  const archived = source.isActive === false;
  const collectionOn = Boolean(source.collectionEnabled) && !archived;

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    setErrorMessage("");
    setPreview(null);
    setConfirmName("");
    setAcknowledged(false);
    previewVendorDirectoryCollectionSourceDeletion(clientViewId, source.id)
      .then((body) => {
        if (!cancelled) setPreview(body.preview || body);
      })
      .catch((error) => {
        if (!cancelled) setErrorMessage(error.message || t(`${COPY_PREFIX}.modal.previewError`));
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId, source.id, t]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busyAction) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [busyAction, onClose]);

  const runUpdate = async (action, patch) => {
    setBusyAction(action);
    setErrorMessage("");
    try {
      const body = await updateVendorDirectoryCollectionSource(clientViewId, source.id, patch);
      onUpdated(body.source || { ...source, ...patch });
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.modal.actionError`));
    } finally {
      setBusyAction("");
    }
  };

  const handleDelete = async () => {
    if (!preview?.deletable || confirmName.trim() !== displayName || !acknowledged) return;
    setBusyAction("delete");
    setErrorMessage("");
    try {
      await deleteVendorDirectoryCollectionSource(clientViewId, source.id);
      onDeleted(source);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.modal.deleteError`));
      setBusyAction("");
    }
  };

  const impactLabel = (line) => {
    const key = line.key ? `${COPY_PREFIX}.impact.${line.key}` : "";
    return key && exists(key) ? t(key) : line.label;
  };

  return createPortal(
    <div className="cvd-modal-backdrop" role="presentation" onClick={() => !busyAction && onClose()}>
      <div
        className="cvd-modal cvd-source-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cvd-modal__header">
          <div>
            <p className="cvd-modal__eyebrow">{t(`${COPY_PREFIX}.modal.eyebrow`)}</p>
            <h2 id={titleId}>{displayName}</h2>
            <p className="muted">{source.sourceKey}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="cvd-btn cvd-btn--ghost"
            onClick={onClose}
            disabled={Boolean(busyAction)}
          >
            {t(`${COPY_PREFIX}.modal.close`)}
          </button>
        </header>

        <div className="cvd-modal__body">
          {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

          <section className="cvd-source-modal__section">
            <h3>{t(`${COPY_PREFIX}.modal.whatItDoes`)}</h3>
            <p>{summary}</p>
            {detail ? <p className="muted">{detail}</p> : null}
          </section>

          <dl className="cvd-dl cvd-source-modal__facts">
            <div>
              <dt>{t(`${COPY_PREFIX}.columns.type`)}</dt>
              <dd>{localizedLabel(t, exists, "sourceTypes", source.sourceType)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.columns.legalUse`)}</dt>
              <dd>{localizedLabel(t, exists, "legalUse", source.legalUseStatus)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.columns.collection`)}</dt>
              <dd>{collectionOn ? t(`${COPY_PREFIX}.collection.enabled`) : t(`${COPY_PREFIX}.collection.disabled`)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.columns.health`)}</dt>
              <dd>
                <span className={`cvd-badge ${healthBadgeClass(source.computedHealth || source.healthStatus)}`}>
                  {localizedLabel(t, exists, "health", source.computedHealth || source.healthStatus)}
                </span>
              </dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.modal.status`)}</dt>
              <dd>{archived ? t(`${COPY_PREFIX}.lifecycle.archived`) : t(`${COPY_PREFIX}.lifecycle.active`)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.columns.lastRun`)}</dt>
              <dd>{source.lastRunCompletedAt ? formatClientDate(source.lastRunCompletedAt, locale) : "—"}</dd>
            </div>
          </dl>

          {canUpdate ? (
            <section className="cvd-source-modal__section">
              <h3>{t(`${COPY_PREFIX}.modal.controls`)}</h3>
              <div className="cvd-source-modal__actions">
                {archived ? (
                  <div className="cvd-source-modal__action">
                    <div>
                      <strong>{t(`${COPY_PREFIX}.actions.restore.title`)}</strong>
                      <p>{t(`${COPY_PREFIX}.actions.restore.hint`)}</p>
                    </div>
                    <button
                      type="button"
                      className="cvd-btn cvd-btn--secondary"
                      disabled={Boolean(busyAction)}
                      onClick={() => runUpdate("restore", { isActive: true })}
                    >
                      {busyAction === "restore" ? t(`${COPY_PREFIX}.actions.working`) : t(`${COPY_PREFIX}.actions.restore.label`)}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cvd-source-modal__action">
                      <div>
                        <strong>
                          {collectionOn
                            ? t(`${COPY_PREFIX}.actions.disable.title`)
                            : t(`${COPY_PREFIX}.actions.enable.title`)}
                        </strong>
                        <p>
                          {collectionOn
                            ? t(`${COPY_PREFIX}.actions.disable.hint`)
                            : t(`${COPY_PREFIX}.actions.enable.hint`)}
                        </p>
                      </div>
                      {collectionOn ? (
                        <button
                          type="button"
                          className="cvd-btn cvd-btn--secondary"
                          disabled={Boolean(busyAction) || !canDeactivate}
                          onClick={() => runUpdate("disable", { collectionEnabled: false })}
                        >
                          {busyAction === "disable"
                            ? t(`${COPY_PREFIX}.actions.working`)
                            : t(`${COPY_PREFIX}.actions.disable.label`)}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="cvd-btn cvd-btn--secondary"
                          disabled={Boolean(busyAction) || !canActivate}
                          onClick={() => runUpdate("enable", { collectionEnabled: true })}
                        >
                          {busyAction === "enable"
                            ? t(`${COPY_PREFIX}.actions.working`)
                            : t(`${COPY_PREFIX}.actions.enable.label`)}
                        </button>
                      )}
                    </div>
                    <div className="cvd-source-modal__action">
                      <div>
                        <strong>{t(`${COPY_PREFIX}.actions.archive.title`)}</strong>
                        <p>{t(`${COPY_PREFIX}.actions.archive.hint`)}</p>
                      </div>
                      <button
                        type="button"
                        className="cvd-btn cvd-btn--secondary"
                        disabled={Boolean(busyAction) || !canDeactivate}
                        onClick={() => runUpdate("archive", { isActive: false, collectionEnabled: false })}
                      >
                        {busyAction === "archive"
                          ? t(`${COPY_PREFIX}.actions.working`)
                          : t(`${COPY_PREFIX}.actions.archive.label`)}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </section>
          ) : null}

          {canUpdate ? (
            <section className="cvd-source-modal__section cvd-source-modal__section--danger">
              <h3>{t(`${COPY_PREFIX}.actions.delete.title`)}</h3>
              <p>{t(`${COPY_PREFIX}.actions.delete.hint`, { name: displayName })}</p>
              {loadingPreview ? <p className="muted">{t(`${COPY_PREFIX}.modal.checkingImpact`)}</p> : null}
              {!loadingPreview && preview?.blockedReason ? <p>{preview.blockedReason}</p> : null}
              {!loadingPreview && preview ? (
                <div className="cvd-source-modal__impact">
                  {[
                    ["removed", preview.removed, t(`${COPY_PREFIX}.impact.removedEmpty`)],
                    ["unlinked", preview.unlinked, t(`${COPY_PREFIX}.impact.unlinkedEmpty`)],
                    ["retained", preview.retained, t(`${COPY_PREFIX}.impact.retainedEmpty`)],
                  ].map(([group, lines, empty]) => (
                    <div key={group}>
                      <h4>{t(`${COPY_PREFIX}.impact.${group}`)}</h4>
                      {lines?.length ? (
                        <ul>
                          {lines.map((line) => (
                            <li key={`${line.action}-${line.key || line.label}`}>
                              <strong>{Number(line.count || 0).toLocaleString(locale)}</strong> {impactLabel(line)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="muted">{empty}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
              {preview?.deletable ? (
                <div className="cvd-source-modal__confirm">
                  <label className="cvd-vendor-create__switch">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      disabled={Boolean(busyAction)}
                      onChange={(event) => setAcknowledged(event.target.checked)}
                    />
                    <span>
                      <strong>{t(`${COPY_PREFIX}.actions.delete.acknowledge`)}</strong>
                    </span>
                  </label>
                  <label className="cvd-field">
                    <span>{t(`${COPY_PREFIX}.actions.delete.typeName`, { name: displayName })}</span>
                    <input
                      type="text"
                      value={confirmName}
                      autoComplete="off"
                      disabled={Boolean(busyAction)}
                      onChange={(event) => setConfirmName(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="cvd-btn cvd-btn--danger"
                    disabled={
                      Boolean(busyAction) || !acknowledged || confirmName.trim() !== displayName
                    }
                    onClick={handleDelete}
                  >
                    {busyAction === "delete"
                      ? t(`${COPY_PREFIX}.actions.deleting`)
                      : t(`${COPY_PREFIX}.actions.delete.label`)}
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ClientVendorDirectorySourceListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t, exists, locale } = useLocale();
  const { permissions } = useVendorDirectoryPermissions();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const basePath = `${serviciosBase}`;
  const canUpdate = hasVendorDirectoryPermission(permissions, VENDOR_DIRECTORY_PERMISSIONS.SOURCES_UPDATE);
  const canActivate = hasVendorDirectoryPermission(permissions, VENDOR_DIRECTORY_PERMISSIONS.SOURCES_ACTIVATE);
  const canDeactivate =
    hasVendorDirectoryPermission(permissions, VENDOR_DIRECTORY_PERMISSIONS.SOURCES_DEACTIVATE) || canUpdate;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryCollectionSources(clientViewId);
      setSources(body.sources || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = sources.find((source) => source.id === selectedId) || null;

  const handleUpdated = (updated) => {
    setSources((current) => current.map((source) => (source.id === updated.id ? { ...source, ...updated } : source)));
  };

  const handleDeleted = (deleted) => {
    setSources((current) => current.filter((source) => source.id !== deleted.id));
    setSelectedId(null);
  };

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`)}</p>
        </div>
        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_CONFIGURE}>
          <div className="cvd-admin__actions">
            <Link className="btn btn-secondary" to={`${basePath}/colecciones/planes`}>
              {t(`${COPY_PREFIX}.plans`)}
            </Link>
          </div>
        </VendorDirectoryPermissionGate>
      </div>

      {loading && <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />}
      {!loading && errorMessage && <ClientPortalError message={errorMessage} onRetry={load} />}
      {!loading && !errorMessage && (
        <div className="cvd-table-wrap">
          <table className="cvd-table">
            <thead>
              <tr>
                <th>{t(`${COPY_PREFIX}.columns.source`)}</th>
                <th>{t(`${COPY_PREFIX}.columns.type`)}</th>
                <th>{t(`${COPY_PREFIX}.columns.legalUse`)}</th>
                <th>{t(`${COPY_PREFIX}.columns.connector`)}</th>
                <th>{t(`${COPY_PREFIX}.columns.collection`)}</th>
                <th>{t(`${COPY_PREFIX}.columns.health`)}</th>
                <th>{t(`${COPY_PREFIX}.columns.lastRun`)}</th>
              </tr>
            </thead>
            <tbody>
              {sources.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    {t(`${COPY_PREFIX}.empty`)}
                  </td>
                </tr>
              ) : (
                sources.map((source) => {
                  const name = sourceField(t, exists, source, "name", source.name);
                  const archived = source.isActive === false;
                  return (
                    <tr
                      key={source.id}
                      className={`cvd-table__row--interactive${selectedId === source.id ? " is-selected" : ""}`}
                      tabIndex={0}
                      onClick={() => setSelectedId(source.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedId(source.id);
                        }
                      }}
                    >
                      <td>
                        <strong>{name}</strong>
                        <div className="cvd-table__meta">
                          {source.sourceKey}
                          {archived ? ` · ${t(`${COPY_PREFIX}.lifecycle.archived`)}` : ""}
                        </div>
                      </td>
                      <td>{localizedLabel(t, exists, "sourceTypes", source.sourceType)}</td>
                      <td>{localizedLabel(t, exists, "legalUse", source.legalUseStatus)}</td>
                      <td>{source.connectorKey || "—"}</td>
                      <td>
                        {source.collectionEnabled && !archived
                          ? t(`${COPY_PREFIX}.collection.enabled`)
                          : t(`${COPY_PREFIX}.collection.disabled`)}
                      </td>
                      <td>
                        <span className={`cvd-badge ${healthBadgeClass(source.computedHealth || source.healthStatus)}`}>
                          {localizedLabel(t, exists, "health", source.computedHealth || source.healthStatus)}
                        </span>
                      </td>
                      <td>{source.lastRunCompletedAt ? formatClientDate(source.lastRunCompletedAt, locale) : "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {selected ? (
        <SourceManageModal
          source={selected}
          clientViewId={clientViewId}
          canUpdate={canUpdate}
          canActivate={canActivate}
          canDeactivate={canDeactivate}
          onClose={() => setSelectedId(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
          t={t}
          exists={exists}
          locale={locale}
        />
      ) : null}
    </div>
  );
}
